import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import {
  SwipeableProvider,
  SwipeableSlider,
  useSwipeable,
} from '@jouzen/ecom-components/v3/SwipeableSlider';
import { Tag, type TagProps } from '@jouzen/ecom-components/v3/Tag';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import PriceInfo from '@/app/components/PriceInfo';
import SwipeableNavButtons from '@/app/components/SwipeableNavButtons';
import type { SupportedCurrency } from '@/app/types/currency';

import type { StoreProductCardProps } from './StoreProductCard';
import { StoreProductCard } from './StoreProductCard';

export interface StoreRingCarouselProps {
  readonly title?: MessageKey;
  readonly tag?: TagProps;
  readonly priceInfo?: {
    currencyCode: SupportedCurrency;
    overviewPrice: number | null | undefined;
    priceText?: MessageKey | null | undefined;
  };
  readonly buttonClassName?: string;
  readonly carouselContainerClassName?: string;
  readonly onClick?: () => void;
  readonly productComponents: StoreProductCardProps[];
}

const StoreRingCarousel = ({
  title,
  tag,
  priceInfo,
  carouselContainerClassName = '',
  productComponents,
}: StoreRingCarouselProps) => {
  const t = useTranslations();
  const { canScrollNext, canScrollPrev } = useSwipeable();

  return (
    <LayoutGrid>
      <GridItem
        colEnd={{ sm: 'full' }}
        colStart={{ sm: 'full' }}
        rowStart={{ sm: 1 }}
        className="px-6 md:px-15"
      >
        <div
          className="flex flex-row justify-between"
          data-cy="title-and-controls-container"
        >
          <div className="flex flex-col items-start">
            {tag && (
              <Tag variant={tag.variant} size="small" data-cy="carousel-tag">
                {tag.children}
              </Tag>
            )}

            <div className="justify-around">
              {title && (
                <Typography
                  className="py-2 text-2xl md:text-6xl md:font-light"
                  data-cy="carousel-title"
                  Element="h2"
                >
                  {t(title)}
                </Typography>
              )}

              {priceInfo && (
                <PriceInfo
                  priceClassName="text-base md:text-xl text-gray-400"
                  currencyCode={priceInfo.currencyCode}
                  basePrice={priceInfo.overviewPrice}
                  priceText={priceInfo.priceText}
                />
              )}
            </div>
          </div>
          <div
            className={cx('self-end max-md:hidden', {
              invisible: !canScrollNext && !canScrollPrev,
            })}
          >
            <SwipeableNavButtons
              buttonClassName="px-4 py-5"
              className="pb-0"
              color="sandstoneOutline"
              data-cy="carousel-nav-buttons"
            />
          </div>
        </div>
      </GridItem>
      <GridItem
        colEnd={{ sm: 'full' }}
        colStart={{ sm: 1, lg: 'full' }}
        rowStart={{ sm: 2 }}
        className={carouselContainerClassName}
      >
        <SwipeableSlider
          className="relative snap-none snap-normal overflow-y-hidden lg:gap-12"
          data-cy="carousel-slider"
          slideClassName="snap-align-none first:pl-6 last:pr-6 md:first:pl-[54px] md:last:pr-[54px] lg:first:pl-23 lg:last:pr-23"
        >
          {productComponents.map((product) => (
            <StoreProductCard key={product.productKey} {...product} />
          ))}
        </SwipeableSlider>
      </GridItem>
    </LayoutGrid>
  );
};

const StoreRingCarouselWrapper = (props: StoreRingCarouselProps) => {
  return (
    <SwipeableProvider>
      <StoreRingCarousel {...props} />
    </SwipeableProvider>
  );
};

export default StoreRingCarouselWrapper;
